import { TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Card, CardBody } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/feedback/EmptyState'
import { PageHeader } from '@/components/layout/PageHeader'

export interface CrashPageProps {
  error: Error
  onReset: () => void
}

/* What a user sees when the interface itself failed, rather than a request.
 *
 * The voice is the same as every other error surface: say what happened, then
 * what to do, and never apologise (EXPERIENCE.md § Voice and Tone). What is
 * different here is the second sentence — a crashed render tells us nothing
 * about whether the work in flight was saved, so the page must not claim it
 * was. "The data was not changed" is true of a failed request and is NOT true
 * of this, so errors.crash.detail says only what we know.
 *
 * Two actions, because the two failure shapes need different escapes. A
 * transient crash — one bad value in one list — clears on a remount, so "Try
 * again" is offered first and costs nothing. A crash in something the shell
 * itself depends on throws again immediately, and then the only way out is a
 * full reload, which is the second action.
 */
export function CrashPage({ error, onReset }: CrashPageProps) {
  const { t } = useTranslation()

  return (
    <>
      <PageHeader title={t('errors.crash.title')} />
      <Card className="mt-6">
        <CardBody>
          <EmptyState
            icon={TriangleAlert}
            title={t('errors.crash.heading')}
            description={t('errors.crash.detail')}
            action={
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button variant="secondary" size="sm" onClick={onReset}>
                  {t('common.actions.retry')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => window.location.reload()}
                >
                  {t('errors.crash.reload')}
                </Button>
              </div>
            }
          />

          {/* The message is a developer's English string from a stack trace, so
              it is never presented as prose to a Russian user (AD-22/AD-27 hold
              the same line for server problem documents). It is here for the
              one thing it is good for: something exact to paste into a report.
              Behind a <details>, closed, in mono, so it is available without
              being the first thing the reader meets. */}
          {error.message ? (
            <details className="border-border-subtle mx-auto mt-4 max-w-[60ch] border-t pt-3">
              <summary className="text-text-muted cursor-pointer text-center text-[12.5px] font-light">
                {t('errors.crash.details')}
              </summary>
              <pre className="asa-scroll text-text-muted mt-2 overflow-x-auto rounded-[var(--radius-sm)] bg-surface-page p-3 text-left font-mono text-[11.5px] leading-relaxed whitespace-pre-wrap">
                {error.message}
              </pre>
            </details>
          ) : null}
        </CardBody>
      </Card>
    </>
  )
}
